import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import type { Mesh, PointLight } from "three";

interface StarBirthProps {
  position: [number, number, number];
  hue?: number;
  /** Called once the birth animation settles (roughly 2s). */
  onSettled?: () => void;
}

/**
 * Animated star birth: starts at scale 0, flashes bright, expands outward
 * with a ring of particles (emissive bloom), then contracts to resting size.
 * The whole sequence is ~2 seconds driven by useFrame.
 */
export function StarBirth({ position, hue = 210, onSettled }: StarBirthProps) {
  const meshRef = useRef<Mesh>(null);
  const lightRef = useRef<PointLight>(null);
  const startTime = useRef<number | null>(null);
  const settled = useRef(false);

  const color = `hsl(${hue}, 80%, 75%)`;
  const coreColor = `hsl(${hue}, 60%, 95%)`;

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;
    const mesh = meshRef.current;
    const light = lightRef.current;
    if (!mesh || !light) return;

    if (t < 0.3) {
      // Phase 1: rapid expansion with bright flash
      const p = t / 0.3;
      const eased = 1 - Math.pow(1 - p, 3);
      mesh.scale.setScalar(eased * 3.5);
      light.intensity = eased * 12;
      (mesh.material as any).emissiveIntensity = 2 + eased * 6;
    } else if (t < 0.8) {
      // Phase 2: contract from oversize to normal with flickering
      const p = (t - 0.3) / 0.5;
      const eased = 1 - Math.pow(1 - p, 2);
      const scale = 3.5 - eased * 2.5;
      const flicker = 1 + Math.sin(t * 40) * 0.08 * (1 - p);
      mesh.scale.setScalar(scale * flicker);
      light.intensity = 12 - eased * 9;
      (mesh.material as any).emissiveIntensity = 8 - eased * 6;
    } else if (t < 2.0) {
      // Phase 3: gentle settle to resting size with fading glow
      const p = (t - 0.8) / 1.2;
      const eased = 1 - Math.pow(1 - p, 2);
      mesh.scale.setScalar(1.0 + (1 - eased) * 0.15);
      light.intensity = 3 - eased * 2.2;
      (mesh.material as any).emissiveIntensity = 2 - eased * 1.2;
    } else {
      // Settled — pulse like a normal star
      const pulse = 1 + Math.sin(clock.elapsedTime * 0.8) * 0.05;
      mesh.scale.setScalar(pulse);
      light.intensity = 0.8;
      (mesh.material as any).emissiveIntensity = 0.8;
      if (!settled.current) {
        settled.current = true;
        onSettled?.();
      }
    }
  });

  return (
    <group position={position}>
      <pointLight ref={lightRef} color={color} intensity={0} distance={20} />
      <mesh ref={meshRef} scale={0}>
        <sphereGeometry args={[0.5, 24, 24]} />
        <meshStandardMaterial
          color={color}
          emissive={coreColor}
          emissiveIntensity={0}
          toneMapped={false}
        />
      </mesh>
      <BurstRing hue={hue} />
      <Sparkles hue={hue} count={12} />
    </group>
  );
}

function Sparkles({ hue, count }: { hue: number; count: number }) {
  const directions = useMemo(() => {
    const dirs: [number, number, number][] = [];
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + Math.random() * 0.3;
      const speed = 2 + Math.random() * 3;
      dirs.push([
        Math.cos(a) * speed,
        (Math.random() - 0.3) * speed * 0.6,
        Math.sin(a) * speed,
      ]);
    }
    return dirs;
  }, [count]);

  return (
    <>
      {directions.map((dir, i) => (
        <Spark key={i} direction={dir} hue={hue} />
      ))}
    </>
  );
}

function Spark({
  direction,
  hue,
}: {
  direction: [number, number, number];
  hue: number;
}) {
  const ref = useRef<Mesh>(null);
  const startTime = useRef<number | null>(null);

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;
    const mesh = ref.current;
    if (!mesh) return;

    if (t < 0.8) {
      const p = t / 0.8;
      mesh.position.set(
        direction[0] * p,
        direction[1] * p,
        direction[2] * p
      );
      mesh.scale.setScalar(Math.max(0, 0.08 * (1 - p)));
      (mesh.material as any).opacity = 1 - p;
    } else {
      mesh.scale.setScalar(0);
    }
  });

  return (
    <mesh ref={ref} scale={0}>
      <sphereGeometry args={[1, 6, 6]} />
      <meshBasicMaterial
        color={`hsl(${hue + Math.random() * 30 - 15}, 90%, 85%)`}
        transparent
        opacity={1}
        toneMapped={false}
      />
    </mesh>
  );
}

function BurstRing({ hue }: { hue: number }) {
  const ref = useRef<Mesh>(null);
  const startTime = useRef<number | null>(null);

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;
    const ring = ref.current;
    if (!ring) return;

    if (t < 1.2) {
      const p = t / 1.2;
      const scale = p * 6;
      ring.scale.setScalar(scale);
      (ring.material as any).opacity = Math.max(0, 0.6 - p * 0.6);
    } else {
      (ring.material as any).opacity = 0;
    }
  });

  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]} scale={0}>
      <ringGeometry args={[0.8, 1.0, 32]} />
      <meshBasicMaterial
        color={`hsl(${hue}, 90%, 80%)`}
        transparent
        opacity={0}
        toneMapped={false}
      />
    </mesh>
  );
}
